/**
 * Line Shares — pure rules (RP-24, approved 2026-10-17).
 *
 * A Plan Line names one Suggested Resource, or — when the RM splits it — two or three
 * Shares, each a Resource covering some of the line's hours each month with an RM-entered
 * points figure. See src/db/schema/plan-line-shares.ts for the data and its boundaries.
 *
 * Everything that needs to know "who is on this line, for which hours" reads
 * `lineResources` rather than `suggested_resource_id`. A split line has no Suggested
 * Resource, so a reader that still looks only at that column treats it as nobody's line —
 * which is exactly the bug this module exists to make impossible to write by accident.
 *
 * No database here; the API loads the inputs.
 */

import { parseHoursCenti } from "@/lib/resource-plan-math";

/** A line holds no Shares, or between MIN and the configured maximum. */
export const MIN_PLAN_LINE_SHARES = 2;
/** The default cap (system_config `max_plan_line_shares` overrides it). */
export const DEFAULT_MAX_PLAN_LINE_SHARES = 3;

const PRIMARY_POSITIONS: readonly string[] = ["R1", "R2", "R3", "R4"];

/** True for R1–R4. A Primary Position is one Resource, so its line can never be split. */
export function isPrimaryPosition(position: string | null): boolean {
  return position !== null && PRIMARY_POSITIONS.includes(position);
}

export const PRIMARY_SPLIT_REFUSAL = "Primary Positions cannot be split. Add a Position instead.";

export type EntryMode = "MonthByMonth" | "SteadyState";

export interface HoursCell {
  /** `YYYY-MM-01` — the API pins months before calling in. */
  month: string;
  hours: string;
}

export interface ShareInput {
  resourceId: string;
  points: number;
  /** MonthByMonth: the Share's cells. Ignored in SteadyState. */
  months: readonly HoursCell[];
  /** SteadyState: the Share's part of the line's run-rate. Ignored in MonthByMonth. */
  steadyStateHours: string | null;
}

export interface LineHoursInput {
  months: readonly HoursCell[];
  steadyStateHours: string | null;
}

/**
 * month → ENTERED (pre-ramp) centi-hours. Shares and lines are compared in this grain: the
 * ramp scales both by the same factor, so comparing before it is exact and comparing after
 * it would invent a centi-hour of rounding difference.
 */
export function enteredByMonth(
  input: LineHoursInput,
  planMonths: readonly string[],
  entryMode: EntryMode,
): Map<string, number> {
  const out = new Map<string, number>();
  if (entryMode === "SteadyState") {
    if (input.steadyStateHours === null) return out;
    const centi = parseHoursCenti(input.steadyStateHours);
    for (const m of planMonths) out.set(m, centi);
    return out;
  }
  for (const cell of input.months) {
    out.set(cell.month, (out.get(cell.month) ?? 0) + parseHoursCenti(cell.hours));
  }
  return out;
}

export interface ShareMonthReconciliation {
  month: string;
  lineCenti: number;
  sharedCenti: number;
  /** Line hours nobody covers this month. Never negative. */
  unallocatedCenti: number;
  /** Share hours beyond the line this month. Never negative. */
  overCenti: number;
}

export interface ShareReconciliation {
  months: ShareMonthReconciliation[];
  unallocatedCenti: number;
  overCenti: number;
}

/**
 * The per-month "Shares sum to the line" check (RP-24.1, RP-24.3). Evaluated month by
 * month, so a hand-off — Resource A in months 1–3, B in 4–6 — is fully allocated. A Share
 * with hours in a month the line has none is over-allocation in that month.
 *
 * An unsplit line (no Shares) reconciles to nothing: it has no Share hours to compare.
 */
export function reconcileShares(
  line: LineHoursInput,
  shares: readonly ShareInput[],
  planMonths: readonly string[],
  entryMode: EntryMode,
): ShareReconciliation {
  if (shares.length === 0) return { months: [], unallocatedCenti: 0, overCenti: 0 };
  const lineByMonth = enteredByMonth(line, planMonths, entryMode);
  const sharedByMonth = new Map<string, number>();
  for (const share of shares) {
    for (const [month, centi] of enteredByMonth(share, planMonths, entryMode)) {
      sharedByMonth.set(month, (sharedByMonth.get(month) ?? 0) + centi);
    }
  }
  const allMonths = [...new Set([...lineByMonth.keys(), ...sharedByMonth.keys()])].sort();
  const months: ShareMonthReconciliation[] = [];
  let unallocatedCenti = 0;
  let overCenti = 0;
  for (const month of allMonths) {
    const lineCenti = lineByMonth.get(month) ?? 0;
    const sharedCenti = sharedByMonth.get(month) ?? 0;
    const unallocated = Math.max(0, lineCenti - sharedCenti);
    const over = Math.max(0, sharedCenti - lineCenti);
    unallocatedCenti += unallocated;
    overCenti += over;
    months.push({ month, lineCenti, sharedCenti, unallocatedCenti: unallocated, overCenti: over });
  }
  return { months, unallocatedCenti, overCenti };
}

/**
 * The structural rules for a Share set, checked before anything is written. Returns the
 * sentence to show, or null when the set may be saved. Over-allocation is checked
 * separately (`reconcileShares`) because it depends on the line's hours.
 */
export function shareSetRefusal(input: {
  position: string | null;
  shares: ReadonlyArray<Pick<ShareInput, "resourceId" | "points">>;
  max: number;
}): string | null {
  const { shares } = input;
  if (shares.length === 0) return null;
  if (isPrimaryPosition(input.position)) return PRIMARY_SPLIT_REFUSAL;
  if (shares.length > input.max) {
    return `A line can be split across at most ${input.max} Resources.`;
  }
  const seen = new Set<string>();
  for (const s of shares) {
    if (seen.has(s.resourceId)) return "Each Resource can hold one Share of a line.";
    seen.add(s.resourceId);
    if (!Number.isInteger(s.points) || s.points < 0) {
      return "Points must be a whole number, 0 or more.";
    }
  }
  return null;
}

export interface LineResourceEntry {
  resourceId: string;
  /**
   * The line's Suggested Resource carries its own Confirmed flag. A Share is an RM's
   * staffing decision in itself, so it is always confirmed.
   */
  confirmed: boolean;
  months: readonly HoursCell[];
  steadyStateHours: string | null;
  /** RM-entered points, for a Share. Null for a Suggested Resource (no points on a line). */
  points: number | null;
  viaShare: boolean;
}

/**
 * Who is on a line, and for which hours. Unsplit: the Suggested Resource carrying all of
 * the line's hours (or nobody). Split: one entry per Share.
 */
export function lineResources(line: {
  suggestedResourceId: string | null;
  suggestedResourceConfirmed: boolean;
  months: readonly HoursCell[];
  steadyStateHours: string | null;
  shares: readonly ShareInput[];
}): LineResourceEntry[] {
  if (line.shares.length > 0) {
    return line.shares.map((s) => ({
      resourceId: s.resourceId,
      confirmed: true,
      months: s.months,
      steadyStateHours: s.steadyStateHours,
      points: s.points,
      viaShare: true,
    }));
  }
  if (line.suggestedResourceId === null) return [];
  return [
    {
      resourceId: line.suggestedResourceId,
      confirmed: line.suggestedResourceConfirmed,
      months: line.months,
      steadyStateHours: line.steadyStateHours,
      points: null,
      viaShare: false,
    },
  ];
}

/**
 * One portion of a split line's month for costing: the entered hours a Share covers that
 * month and the cost rate (cents/hr) it is priced at, or null for the Labor Category cost.
 */
export interface CostPortion {
  enteredCenti: number;
  costCents: number | null;
}

/**
 * month → the cost portions of a split line. `costCentsFor(shareIndex, month)` returns the
 * Share holder's Internal Rate in effect (already pooled by the two-Resource rule), or null.
 * Unallocated hours need no portion: the math prices whatever the portions leave at the
 * Labor Category cost.
 */
export function costPortionsByMonth(
  shares: readonly ShareInput[],
  planMonths: readonly string[],
  entryMode: EntryMode,
  costCentsFor: (shareIndex: number, month: string) => number | null,
): Map<string, CostPortion[]> {
  const out = new Map<string, CostPortion[]>();
  for (const [i, share] of shares.entries()) {
    for (const [month, enteredCenti] of enteredByMonth(share, planMonths, entryMode)) {
      if (enteredCenti <= 0) continue;
      const list = out.get(month) ?? [];
      list.push({ enteredCenti, costCents: costCentsFor(i, month) });
      out.set(month, list);
    }
  }
  return out;
}

/**
 * "Ada Lovelace" → "Ada L". First name and last initial; a one-word name stays whole.
 * The parent row of a split line shows its Share holders this way (Martyn, 2026-10-17).
 */
export function shortResourceName(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((p) => p.length > 0);
  const [first] = parts;
  if (first === undefined) return "";
  const last = parts.length > 1 ? parts[parts.length - 1] : undefined;
  const initial = last?.[0];
  return initial === undefined ? first : `${first} ${initial.toUpperCase()}`;
}

/** "Ada L / Bo M / Cy N" — the parent row's summary of a split line. */
export function formatShareHolders(names: readonly string[]): string {
  return names.map(shortResourceName).join(" / ");
}
